<template>
  <card-container
    :id="pageId"
    :class="pageId"
    :is-loading="status.isLoading"
    :loading-message="status.loadingMessage"
    :no-title="isNoTitle"
    button-location="top"
    @submit="redirectPage"
  >
    <template #title>
      Ride History
    </template>

    <template #top-form>
      <fieldset>
        <div
          v-if="!isPartnerDashboard && !isFraudCheck"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Search Type* </label>
          <div class="col-sm-3">
            <dropdown-list
              v-model="searchConditions.queryType"
              :options="queryTypeList"
            />
          </div>
        </div>

        <div
          v-if="!isFraudCheck"
          :class="`row ${!isSearchByDriver ? 'align-items-center' : ''}`"
        >
          <label
            class="col-sm-3 control-label"
            :class="{ 'pt-10': isSearchByDriver }"
          >
            {{ searchFieldLabel }}
          </label>
          <div
            v-if="isShowAddressField"
            class="col-sm-6"
          >
            <address-autocomplete-input
              v-model="searchConditions.queryInfo"
              address-type="Full Address"
              :bounds="anchorBound"
              @place-changed="setAddressData"
            />
          </div>

          <div
            v-else-if="isSearchByOperator"
            class="col-sm-6"
          >
            <dropdown-list
              v-model="searchConditions.queryInfo"
              required
              filterable
              :options="operatorsList"
            />
          </div>

          <template v-else-if="isSearchByDriver">
            <div class="col-sm-6">
              <sanitized-input
                v-model="searchConditions.queryInfo"
                placeholder="Please input search value"
                :required="!isPartnerDashboard"
              />

              <phone-number-input
                id="customer-phone"
                v-model="searchConditions.customerPhoneNumber"
                class="mt-10"
                :page-id="pageId"
              >
                <template #help-block>
                  Input a customer's phone number to search for drivers that only this user has taken rides with
                </template>
              </phone-number-input>
            </div>
          </template>

          <div
            v-else
            class="col-sm-6"
          >
            <sanitized-input
              v-model="searchConditions.queryInfo"
              placeholder="Please input search value"
              :required="!isPartnerDashboard"
            />
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Status </label>
          <div class="col-sm-3">
            <dropdown-list
              v-model="searchConditions.status"
              :options="statusTypeList"
              filterable
            >
              <template #additional-options-prefix>
                <el-option
                  label="All Status"
                  value=""
                >
                  All Status
                </el-option>
              </template>
            </dropdown-list>
          </div>
        </div>

        <div class="row align-items-center">
          <label class="col-sm-3 control-label"> Ordered By </label>
          <div class="col-sm-6">
            <dropdown-list
              v-model="searchConditions.orderedBy"
              :options="orderedByList"
              clearable
            />
          </div>
        </div>

        <div
          v-if="isAvailableToSearchForAllPartners"
          class="row align-items-center"
        >
          <div class="col-sm-3 offset-sm-3">
            <el-checkbox
              id="allPartners"
              v-model="searchConditions.isForAllPartners"
            >
              Rides in All Partners
            </el-checkbox>
          </div>
        </div>

        <div
          v-if="!isPartnerDashboard"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Vendor </label>
          <div class="col-sm-2">
            <dropdown-list
              v-model="searchConditions.vendor"
              :options="vendorTypeList"
            >
              <template #additional-options-prefix>
                <el-option
                  label="All Type"
                  value=""
                >
                  All Type
                </el-option>
              </template>
            </dropdown-list>
          </div>

          <div class="col-sm-2 offset-sm-0 col-5 offset-1 mobile-mt-10">
            <el-checkbox v-model="searchConditions.isBuggedRide">
              Bugged Ride
            </el-checkbox>
          </div>

          <div class="col-sm-3 col-6 mobile-mt-10">
            <el-checkbox v-model="searchConditions.isEmergency">
              Emergency Ride
            </el-checkbox>
          </div>
        </div>

        <div
          v-if="!isPartnerDashboard"
          class="row align-items-center"
        >
          <label class="col-sm-3 control-label"> Min Amount </label>
          <div class="col-3">
            <number-input
              id="minAmount"
              v-model="searchConditions.minAmount"
              class="form-control"
              type="number"
              placeholder="Min amount"
              step="0.1"
              :max="searchConditions.maxAmount ? Number(searchConditions.maxAmount) : null"
            />
          </div>

          <label class="control-label">Max Amount</label>
          <div class="col-3">
            <number-input
              id="maxAmount"
              v-model="searchConditions.maxAmount"
              class="form-control"
              type="number"
              placeholder="Max amount"
              step="0.1"
              :min="searchConditions.minAmount ? Number(searchConditions.minAmount) : null"
            />
          </div>
        </div>

        <div class="row">
          <label class="col-sm-3 control-label pt-10"> Time Span </label>
          <div class="col-sm-6">
            <date-picker
              v-model="timeSpanValue"
              type="daterange"
            >
              <template #comment>
                <span class="help-block"> Please click the same day twice if you only want results from that particular day </span>
              </template>
            </date-picker>
          </div>
        </div>

        <div class="form-submit-button-group">
          <p-button
            type="purple"
            round
            native-type="submit"
            :disabled="status.isLoading"
          >
            Search
          </p-button>

          <p-button
            v-if="isPartnerDashboard"
            type="info"
            round
            size="md"
            :disabled="status.isLoading || rides.length === 0"
            @click="downloadRidesJSON2CSV(1)"
          >
            Download
          </p-button>

          <p-button
            type="yellow"
            round
            :disabled="status.isLoading"
            @click="resetForm"
          >
            Reset
          </p-button>
        </div>
      </fieldset>

      <fieldset class="mt-20 mobile-mt-20">
        <div
          v-if="!isPartnerDashboard"
          class="row"
        >
          <el-switch
            v-model="viewMode"
            active-color="#13ce66"
            inactive-color="#13ce66"
            inactive-text="Table View"
            active-value="card"
            inactive-value="table"
            active-text="Card View"
            class="col-sm-3 offset-sm-1"
          />

          <el-switch
            v-model="status.autoLoading"
            active-color="#13ce66"
            active-text="Enable auto-loading"
            inactive-text="Disable auto-loading"
            class="col-sm-5 mobile-mt-10"
            :active-value="true"
            :inactive-value="false"
          />

          <el-switch
            v-model="status.timeZoneMode"
            active-color="#13ce66"
            inactive-color="#13ce66"
            inactive-text="Customer Time"
            inactive-value="customer"
            active-text="UTC Time"
            active-value="utc"
            data-test="timezone-switch"
            class="col-sm-3"
          />
        </div>

        <pagination
          class="mb-10"
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="getRides"
        />
      </fieldset>

      <message-html :result-html="status.resultHtml" />
    </template>

    <template #content>
      <table-view-container
        v-if="viewMode === 'table'"
        row-key="key"
        :page-id="pageId"
        :data="rides"
        :table-columns="tableColumns"
        :fixed-column-counts="isPartnerDashboard ? 0 : 3"
        :selected-index="selectedIndex"
        :is-summary="true"
        :highlight-list="highlightRidesIndexes"
        :custom-cell-style-func="customCellStyle"
        :get-summaries="(param) => getSummaries(param, summaryColumns)"
        @column-click="(row, column) => copyCell(row, column, tableColumns)"
      >
        <template
          v-if="!isFraudCheck"
          #right-fixed-columns
        >
          <el-table-column
            v-if="rides.length > 0"
            fixed="right"
            label="Action"
            width="55"
          >
            <template #default="scope">
              <template v-if="!isPartnerDashboard">
                <el-tooltip
                  v-if="scope.row.requestId"
                  class="item"
                  effect="dark"
                  content="Tip the Driver"
                  placement="left"
                >
                  <div
                    class="info-icon gift-icon"
                    @click="showGiftDialog(scope.$index, scope.row)"
                  >
                    🎁
                  </div>
                </el-tooltip>

                <el-tooltip
                  v-if="scope.row.requestId"
                  class="item"
                  effect="dark"
                  content="Resend Receipt"
                  placement="left"
                >
                  <div
                    class="info-icon receipt-icon"
                    @click="sendReceipt(scope)"
                  >
                    <i class="fa fa-paper-plane" />
                  </div>
                </el-tooltip>
              </template>

              <el-tooltip
                class="item"
                effect="dark"
                content="Re-order Ride"
                placement="left"
              >
                <div
                  class="info-icon reorder-icon border"
                  @click="reorderRide(scope.$index, false)"
                >
                  <i class="fa fa-play" />
                </div>
              </el-tooltip>

              <el-tooltip
                class="item"
                effect="dark"
                content="Order Return Ride"
                placement="left"
              >
                <div
                  class="info-icon return-ride-icon border"
                  @click="reorderRide(scope.$index, true)"
                >
                  <i class="fa fa-exchange" />
                </div>
              </el-tooltip>
            </template>
          </el-table-column>

          <el-table-column
            v-if="rides.length > 0"
            fixed="right"
            label="Info"
            width="50"
          >
            <template #default="scope">
              <el-tooltip
                class="item"
                effect="dark"
                content="Displays activities and details about the Ride"
                placement="left"
              >
                <div
                  class="info-icon border"
                  @click="getRideInfoDetail(scope)"
                >
                  <i class="fa fa-info" />
                </div>
              </el-tooltip>

              <el-tooltip
                v-if="!isPartnerDashboard"
                class="item"
                effect="dark"
                content="File a Need Love"
                placement="left"
              >
                <div
                  class="info-icon nl-icon"
                  @click="showNLDialog(scope)"
                >
                  <i class="fa fa-heart" />
                </div>
              </el-tooltip>

              <el-tooltip
                v-if="!isPartnerDashboard && scope.row.operatorName && isHigherThanOperator"
                class="item"
                effect="dark"
                content="Fill Operator Report"
                placement="left"
              >
                <div
                  class="info-icon fill-icon"
                  @click="gotoOperatorReport(scope.row)"
                >
                  <i class="fa fa-pencil-square-o" />
                </div>
              </el-tooltip>
            </template>
          </el-table-column>
        </template>
      </table-view-container>

      <template v-if="viewMode === 'card'">
        <ride-history-card
          v-if="viewMode === 'card'"
          :rides="rides"
          @goto-scheduled-ride="gotoScheduledRide"
          @show-gift-dialog="showGiftDialog"
        />

        <pagination
          class="mt-20 mb-10"
          :is-loading="status.isLoading"
          :current-page="paginationInfo.currentPage"
          :total-pages="paginationInfo.totalPages"
          @load-page-data="getRides"
        />
      </template>

      <choose-complaint-type-dialog
        :is-show-dialog="status.isShowComplaintTypeDialog"
        @submit="chooseComplaintType"
        @cancel="status.isShowComplaintTypeDialog = false"
      />

      <give-gift-tip-dialog
        :is-show-dialog="status.isShowGiftDialog"
        :ride="selectedRide"
        @submit="giveGiftTip"
        @cancel="status.isShowGiftDialog = false"
      />
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { ref, toRefs, Ref, reactive, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { cloneDeep, concat } from 'lodash';
import Moment from 'moment-timezone';
import { ElNotification } from 'element-plus';
import events from '@/events';
import { Parser } from '@json2csv/plainjs';

import util from '@/Utils/util';
import rideUtility from '@/Utils/rideUtility';
import { useProfile } from '@/use/profile';
import { useDashboard } from '@/use/app';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { useTable } from '@/use/table';
import { useAddress } from '@/use/address';
import { usePartner } from '@/use/partner';
import { useGlobal } from '@/use/global';
import { useRide } from '@/use/ride';
import { sendGet, sendPost } from '@/Utils/api';
import { CustomLocationType } from '@/models/address';
import { TableColumnType } from '@/models/table';
import { ListType } from '@/models/app';
import { ADRS_PARTNERS, GVRA_PARTNERS, NC_EIPD_PARTNERS, PartnerCodeName, VRNH_PARTNERS } from '@/models/partner';
import { vendorTypeList } from '@/constants/app';
import { rideHistoryTableColumnsForOperator, rideHistoryTableColumnsForPartner } from '@/constants/ride';
import { findPartnerField } from '@/Utils/partners/partnerFieldConfig';
import { fetchOperators } from '@/services/operators/fetchOperators';
import { fetchOperator } from '@/services/operators/fetchOperator';
import { blacklistAddressService } from '@/services/blacklistAddress';
import { rideService } from '@/services/ride';
import { operatorReportData } from '@/services/operators/viewModel';

const RideHistoryCard = defineAsyncComponent(() => import('@/components/UIComponents/RideHistoryCardView.vue') as any);

const ChooseComplaintTypeDialog = defineAsyncComponent(() => import('../../Dialogs/ChooseComplaintType.vue') as any);

const GiveGiftTipDialog = defineAsyncComponent(() => import('../../Dialogs/GiveGiftTip.vue') as any);

const pageId = 'ride-search';
const blacklistRadius = 0.01;
const operatorsPaginationLimit = 50;
const tablePaginationLimit: number = 30;
const downloadPaginationLimit: number = 100;

const summaryColumns: string[] = [
  'gogoFee',
  'gogoCost',
  'customerCost',
  'customerRefund',
  'customerCredits',
  'customerCreditsRefund',
  'partnerCost',
  'partnerRefund',
  'partnerCredits',
  'partnerCreditsRefund',
];

const statusTypeList: ListType[] = [
  { id: 1, key: 'accepted', label: 'Accepted' },
  { id: 2, key: 'arriving', label: 'Arriving' },
  { id: 3, key: 'bad_driver', label: 'Bad Driver' },
  { id: 4, key: 'car_too_big', label: 'Car Too Big' },
  { id: 5, key: 'completed', label: 'Completed' },
  { id: 6, key: 'driver_asked_to_cancel', label: 'Driver Asked to Cancel' },
  { id: 7, key: 'driver_canceled', label: 'Driver Canceled' },
  { id: 8, key: 'in_progress', label: 'In Progress' },
  { id: 9, key: 'no_drivers_available', label: 'No Drivers Available' },
  { id: 10, key: 'operator_canceled', label: 'Operator Canceled' },
  { id: 11, key: 'processing', label: 'Processing' },
  { id: 12, key: 'rider_canceled', label: 'Rider Canceled' },
  { id: 13, key: 'start', label: 'Start' },
];

const commonOrderedByList: ListType[] = [
  { id: 1, key: 'operator', label: 'Operator' },
  { id: 2, key: 'user', label: 'User' },
  { id: 3, key: 'auto', label: 'Auto' },
  { id: 4, key: 'supervisor', label: 'Supervisor' },
  { id: 5, key: 'partner', label: 'Partner' },
  { id: 6, key: 'scheduler', label: 'Scheduler' },
  { id: 7, key: 'lossPrevention', label: 'Loss Prevention' },
  { id: 8, key: 'customer', label: 'Customer (via Client dashboard)' },
];

const props = defineProps({
  isNoTitle: {
    type: Boolean,
    default: () => false,
  },
  isFraudCheck: {
    type: Boolean,
    default: () => false,
  },
});

const route = useRoute();
const router = useRouter();

const { userRoles } = useProfile();
const { isPartnerDashboard, dashboardCategory } = useDashboard();
const { customerPhoneNumber, anchorBound } = useCustomer();
const { isHigherThanOperator } = toRefs(userRoles);
const { showSuccessPopupDialog, showErrorPopupDialog, showConfirmPopupDialog } = useSwalDialog();
const { copyCell, getSummaries } = useTable();
const { compareDistances, getFormattedAddress } = useAddress();
const { partnerId, partnerCode, partnerList, partnerFieldConfig } = usePartner();
const { blacklistAddresses, setAutoFillRide, setBlacklistAddresses } = useGlobal();
const { scheduledRideId, setRideId, setRideRequestId, setScheduledRideId, setRideOrderData } = useRide();

const rides: Ref<any> = ref([]);
const originalRides: Ref<any> = ref([]);
const highlightRidesIndexes: Ref<any> = ref([]);
const downloadData: Ref<any> = ref([]);
const timeSpanValue: Ref<any> = ref(null);
const selectedRide: Ref<any> = ref(null);
const selectedIndex = ref(-1);
const viewMode = ref('table');
const paginationInfo = reactive({
  totalPages: 1,
  currentPage: 1,
});
const status = reactive({
  isLoading: false,
  loadingMessage: 'Loading Rides...',
  resultHtml: '',
  autoLoading: true,
  isShowComplaintTypeDialog: false,
  isShowGiftDialog: false,
  isRideActivitiesLoading: false,
  timeZoneMode: 'customer',
});
const searchConditions = reactive({
  queryType: '',
  queryInfo: '',
  minAmount: '',
  maxAmount: '',
  status: '',
  vendor: '',
  startDateTime: '',
  endDateTime: '',
  isBuggedRide: false,
  isEmergency: false,
  orderedBy: '',
  isForAllPartners: false,
  customerPhoneNumber: '',
});
const partnerTableColumns: Ref<TableColumnType[]> = ref([...rideHistoryTableColumnsForPartner]);
const partnerDownloadFields: Ref<string[]> = ref([...rideUtility.rideDownloadFields]);

// Ride history columns backed by a partner-caller field take that field's configured label.
// TODO UNIFY: which columns a partner gets (and the download fields) is still chosen per partner code in onMounted below.
const partnerFieldByRideColumn: Record<string, string> = {
  externalPartnerCallerId: 'externalPartnerCallerId',
  preAuthorizationNumber: 'preAuthorizationNumber',
  serviceCode: 'partnerServiceCode',
  beginningDate: 'partnerBeginningDate',
  suspensionDate: 'partnerSuspensionDate',
  monthlyBudgetAmount: 'partnerMonthlyBudgetAmount',
  counselorFirstName: 'partnerEmergencyContactFirstName',
  counselorLastName: 'partnerEmergencyContactLastName',
  counselorEmail: 'partnerEmergencyContactEmail',
};

const labelledPartnerTableColumns = computed(() =>
  partnerTableColumns.value.map((column) => {
    const label = findPartnerField(partnerFieldConfig.value, partnerFieldByRideColumn[column.field])?.label;

    return label ? { ...column, label } : column;
  }),
);

const queryTypeList: Ref<ListType[]> = ref([]);
const operatorsList: Ref<ListType[]> = ref([]);

const tableColumns = computed(() => (isPartnerDashboard.value ? labelledPartnerTableColumns.value : rideHistoryTableColumnsForOperator));

const searchFieldLabel = computed(() => {
  if (isPartnerDashboard.value) {
    return 'Phone Number';
  }

  const label = util.getLabelByField(queryTypeList.value, searchConditions.queryType);

  return `${label}*`;
});

const isShowAddressField = computed(() => searchConditions.queryType === 'startLocation' || searchConditions.queryType === 'endLocation');

const isSearchByOperator = computed(() => searchConditions.queryType === 'operator-id');

const isAvailableToSearchForAllPartners = computed(() => {
  if (!isPartnerDashboard.value || !partnerList.value) {
    return false;
  }

  return partnerList.value.length > 1;
});

const isSearchByDriver = computed(() => {
  return (
    searchConditions.queryType === 'driver-name' ||
    searchConditions.queryType === 'driver-phone' ||
    searchConditions.queryType === 'driver-plate'
  );
});

const displayingTimeZone = computed(() => (status.timeZoneMode === 'utc' ? 'UTC' : undefined));

const orderedByList = computed(() => {
  if (isPartnerDashboard.value) {
    return [
      ...commonOrderedByList,
      {
        id: 9,
        key: 'phone-customer',
        label: 'Customer (via Phone)',
      },
    ];
  }

  return commonOrderedByList;
});

const initQueryTypeList = () => {
  queryTypeList.value = [
    { id: 1, key: 'phone-number', label: 'Phone Number' },
    { id: 2, key: 'caller-id', label: 'Caller ID' },
    { id: 3, key: 'ride-id', label: 'Ride ID' },
    { id: 4, key: 'request-id', label: 'Request ID' },
    { id: 5, key: 'scheduledRideId', label: 'Scheduled Ride ID' },
    { id: 6, key: 'caller-email', label: 'User E-mail' },
    { id: 7, key: 'lovedone-email', label: 'Loved One E-mail' },
    { id: 8, key: 'driver-name', label: 'Driver Name' },
    { id: 9, key: 'driver-phone', label: 'Driver Phone' },
    { id: 10, key: 'driver-plate', label: 'Driver Plate' },
    { id: 11, key: 'startLocation', label: 'Start Location' },
    { id: 12, key: 'endLocation', label: 'End Location' },
    { id: 13, key: 'operator-id', label: 'Operator' },
  ];
};

const setAddressData = (address: CustomLocationType | null) => {
  searchConditions.queryInfo = address ? getFormattedAddress(address) : '';
};

const gotoScheduledRide = (scheduledRideId: number) => {
  setScheduledRideId(scheduledRideId);

  router.push({
    name: 'ScheduledRides',
    query: {
      phoneNumber: customerPhoneNumber.value,
    },
  });
};

const getBlacklistAddresses = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * 50;

  try {
    const addresses = await blacklistAddressService.fetchBlacklistAddresses({
      pagination: {
        limit: 50,
        offset,
      },
    });

    setBlacklistAddresses([...blacklistAddresses.value, ...addresses.rows]);

    if (blacklistAddresses.value.length < addresses.count) {
      await getBlacklistAddresses(currentPage + 1);
    }
  } catch (error) {
    console.log(util.getGraphqlError(error));
  }
};

const isRideInBlacklistAddress = (ride: any, address: any) => {
  return (
    compareDistances(address.latitude, address.longitude, Number(ride.startLat), Number(ride.startLong), blacklistRadius, '<=') ||
    compareDistances(address.latitude, address.longitude, Number(ride.endLat || 0), Number(ride.endLong || 0), blacklistRadius, '<=')
  );
};

const getRides = async (currentPage: number = 1) => {
  status.resultHtml = '';
  selectedRide.value = null;
  selectedIndex.value = -1;
  highlightRidesIndexes.value = [];
  paginationInfo.currentPage = currentPage;

  const query = makeSearchQuery(currentPage);

  status.isLoading = true;
  status.loadingMessage = 'Loading Rides...';
  if (props.isFraudCheck && blacklistAddresses.value.length === 0) {
    await getBlacklistAddresses();
  }

  try {
    const response = await sendGet('rides', query);
    paginationInfo.totalPages = Math.ceil(response.data.count / tablePaginationLimit) || 1;

    let temporaryRides = response.data.rows;
    originalRides.value = cloneDeep(response.data.rows);

    const requestIds = temporaryRides.map((ride: any) => ride.requestId).filter((requestId: string) => !!requestId);

    if (requestIds.length > 0) {
      const customChargeRecords = await rideService.fetchRideCustomChargeRecords(requestIds);

      rideUtility.formatCustomChargeRecordsAndAddToChildrenOfRide(temporaryRides, customChargeRecords, isPartnerDashboard.value);
    }

    let rowIndex = 0;

    rides.value = temporaryRides.map((ride: any, index: number) => {
      addHighlightRowForUnpaidRide(ride, rowIndex);
      addHighlightRowForPremiumRideType(ride, rowIndex);
      addHighlightRowForRideInBlacklistAddress(ride, rowIndex);
      makeSameColumnColorForSameDateRides(ride, temporaryRides[index + 1]);

      ride.index = (currentPage - 1) * tablePaginationLimit + index + 1;
      ride.orderIndex = index;
      ride = rideUtility.formatRideData(ride, false, displayingTimeZone.value);
      rowIndex = rowIndex + 1 + (ride.children?.length || 0);

      return ride;
    });
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
    rides.value = [];
    originalRides.value = [];
    paginationInfo.currentPage = 1;
    paginationInfo.totalPages = 1;
  }

  status.isLoading = false;
};

const makeSearchQuery = (currentPage: number) => {
  const query: any = {
    type: searchConditions.queryType,
    q: searchConditions.queryInfo,
    status: searchConditions.status,
    orderedBy: searchConditions.orderedBy,
    start: searchConditions.startDateTime,
    end: searchConditions.endDateTime,
    limit: tablePaginationLimit,
    offset: (currentPage - 1) * tablePaginationLimit,
  };

  if (isPartnerDashboard.value) {
    if (searchConditions.isForAllPartners) {
      query.partnerIds = partnerList.value?.map((partner: any) => partner.id).join(',');
    } else {
      query.partnerId = partnerId.value;
    }

    const geocodeFields = ['startLat', 'startLong', 'endLat', 'endLong', 'destinationLat', 'destinationLng'];
    query.fields = [...partnerDownloadFields.value, ...geocodeFields].join(',');
  } else {
    query.vendor = searchConditions.vendor ? searchConditions.vendor.toLowerCase() : null;
    query.isBuggedRide = searchConditions.isBuggedRide ? 1 : null;
    query.isEmergency = searchConditions.isEmergency ? 1 : null;
    query.minAmount = searchConditions.minAmount;
    query.maxAmount = searchConditions.maxAmount;
  }

  if (isSearchByDriver.value) {
    query.phoneNumber = util.getValidPhoneNumber(searchConditions.customerPhoneNumber);
  }

  return query;
};

const addHighlightRowForUnpaidRide = (ride: any, rowIndex: number) => {
  if (isPartnerDashboard.value) {
    return;
  }

  if (!isUnpaidRide(ride)) {
    return;
  }

  highlightRidesIndexes.value.push({
    index: rowIndex,
    class: 'text-red',
    type: 'row',
  });
};

const addHighlightRowForPremiumRideType = (ride: any, rowIndex: number) => {
  if (!props.isFraudCheck) {
    return;
  }

  if (ride.rideType === 'uberX' || ride.rideType === 'lyft') {
    return;
  }

  highlightRidesIndexes.value.push({
    index: rowIndex,
    class: 'orange-background',
    type: 'column',
    field: 'rideType',
  });
};

const addHighlightRowForRideInBlacklistAddress = (ride: any, rowIndex: number) => {
  if (!props.isFraudCheck) {
    return;
  }

  const isBlacklistAddress = blacklistAddresses.value.some((address: any) => isRideInBlacklistAddress(ride, address));

  if (!isBlacklistAddress) {
    return;
  }

  highlightRidesIndexes.value.push({
    index: rowIndex,
    class: 'light-pink-background',
    type: 'row',
  });
};

const makeSameColumnColorForSameDateRides = (ride: any, nextRide: any) => {
  if (!props.isFraudCheck) {
    return;
  }

  if (!nextRide) {
    return;
  }

  const currentRowCreatedAt = util.formatDateTime(ride.createdAt, 'YYYY-MM-DD');
  const nextRowCreatedAt = util.formatDateTime(nextRide.createdAt, 'YYYY-MM-DD');
  if (currentRowCreatedAt === nextRowCreatedAt) {
    if (!ride.createdAtColumnColor) {
      ride.createdAtColumnColor = getRandomColor();
    }

    nextRide.createdAtColumnColor = ride.createdAtColumnColor;
  }
};

const getRandomColor = () => {
  const colorArray = ['#89ecc3', '#a29ae6', '#da9ae6', '#409eff', '#67c23a', '#bdf9e0', '#fb8558', '#eafb58', '#71fb58', '#97f2f3'];
  const randomIndex = Math.floor(Math.random() * 10);

  return colorArray[randomIndex];
};

const showGiftDialog = (index: number, ride: any) => {
  selectedIndex.value = index;
  selectedRide.value = cloneDeep(ride);
  status.isShowGiftDialog = true;
};

const giveGiftTip = async ({ notes, tipAmount }: { notes: string; tipAmount: number }) => {
  status.isShowGiftDialog = false;
  status.loadingMessage = 'Giving Gift to Driver...';
  status.isLoading = true;
  try {
    await rideService.tipDriver({
      notes,
      tipAmount,
      requestId: selectedRide.value.requestId,
    });

    showSuccessPopupDialog({
      text: `$${tipAmount} has been given to the driver successfully.`,
    });
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  status.isLoading = false;
};

const showNLDialog = (scope: any) => {
  selectedIndex.value = scope.$index;
  selectedRide.value = cloneDeep(scope.row);
  status.isShowComplaintTypeDialog = true;
};

const getRideInfoDetail = (scope: any) => {
  if (status.isRideActivitiesLoading) {
    return;
  }

  selectedIndex.value = scope.$index;
  selectedRide.value = cloneDeep(scope.row);
  setRideId(Number(selectedRide.value.id));
  setRideRequestId(selectedRide.value.requestId);
};

const sendReceipt = async (scope: any) => {
  const isSendReceipt = await showConfirmPopupDialog({
    text: 'Are you sure you want to re-send the receipt?',
    title: 'Resend Receipt?',
    confirmButtonText: 'Re-send',
  });

  if (!isSendReceipt) {
    return;
  }

  status.loadingMessage = 'Resending Receipt...';
  status.isLoading = true;
  try {
    await sendPost('sendReceipt', {
      requestId: scope.row.requestId,
    });

    ElNotification({
      message: 'Resent receipt successfully.',
      type: 'success',
      title: 'Success!',
    });
  } catch (error) {
    const message = util.getErrorResponse(error, false);

    ElNotification({
      message,
      type: 'error',
      title: 'Error!',
    });
  }
  status.isLoading = false;
};

const reorderRide = (index: number, isReturnRide: boolean) => {
  const ride = originalRides.value[index];

  if (ride.riderPhone !== customerPhoneNumber.value) {
    // if there is no customer on RightPanel, we have to pull the cx firstly.
    events.emit('refresh-right-panel', {
      phone: ride.riderPhone,
      isAllowRedirection: false,
    });
  }

  const startAddress = util.getAddressObjectFromString(isReturnRide ? ride.endLocation || ride.destination : ride.startLocation);
  const endAddress = util.getAddressObjectFromString(isReturnRide ? ride.startLocation : ride.endLocation || ride.destination);

  const rideData = {
    phoneNumber: customerPhoneNumber.value,
    riderPhone: ride.riderPhone,
    typeOfCar: ride.rideType,
    partnerRidePurpose: ride.partnerRidePurpose,
    serviceType: ride.serviceType,
    rideType: {
      badDrivers: ride.overrideBadDriver,
      overrideBigCar: ride.overrideBigCar,
      overrideSmallCar: ride.overrideSmallCar,
      overrideBannedCar: ride.overrideBannedCar,
    },
    noteToDriver: ride.noteToDriver,
    startAddress: {
      streetAddress: startAddress.streetAddress,
      city: startAddress.city,
      state: startAddress.state,
      zip: startAddress.zip,
      name: '',
      latitude: isReturnRide ? Number(ride.endLat || ride.destinationLat) : Number(ride.startLat),
      longitude: isReturnRide ? Number(ride.endLong || ride.destinationLng) : Number(ride.startLong),
      type: false,
    },
    endAddress: {
      streetAddress: endAddress.streetAddress,
      city: endAddress.city,
      state: endAddress.state,
      zip: endAddress.zip,
      name: '',
      latitude: isReturnRide ? Number(ride.startLat): Number(ride.endLat || ride.destinationLat),
      longitude: isReturnRide ? Number(ride.startLong): Number(ride.endLong || ride.destinationLng),
      type: false,
    },
    isExpandDriverSearch: Boolean(ride.overrideBadDriver),
    isExpandVehicleSearch: Boolean(ride.overrideBannedCar),
  };

  setRideOrderData(rideData);

  router.push({
    path: `/${dashboardCategory.value}/ride-ordering`,
    query: {
      rideFormType: 'order',
    },
  });
};

const chooseComplaintType = () => {
  status.isShowComplaintTypeDialog = false;
  setAutoFillRide(selectedRide.value);

  router.push({
    name: 'NeedLove',
    query: {
      phoneNumber: customerPhoneNumber.value,
    },
  });
};

const gotoOperatorReport = async (ride: any) => {
  const data = {
    rideId: ride.id.toString(),
    customerName: ride.customerName,
    customerPhoneNumber: ride.riderPhone,
  } as any;

  status.isLoading = true;
  status.loadingMessage = 'Loading Operator Info...';
  try {
    const operator = await fetchOperator(ride.orderedById);
    data.operatorName = util.getFullName(operator.firstName, operator.lastName);
    data.teamLeadId = operator.teamLeadId;
  } catch (error) {
    const errorText = util.getGraphqlError(error, false);
    console.log(errorText);
  }
  status.isLoading = false;

  operatorReportData.value = data;

  router.push({
    name: 'CallLogs',
    query: route.query,
    hash: '#operator-report',
  });
};

const redirectPage = () => {
  setRideId(0);
  setRideRequestId('');
  getRides();
};

const downloadRidesJSON2CSV = async (pageIndex: number = 1) => {
  status.isLoading = true;
  status.loadingMessage = 'Please wait, while we prepare your download...';

  const query: any = {
    type: searchConditions.queryType,
    q: searchConditions.queryInfo,
    status: searchConditions.status,
    start: searchConditions.startDateTime,
    end: searchConditions.endDateTime,
    fields: partnerDownloadFields.value.join(','),
    limit: downloadPaginationLimit,
    offset: (pageIndex - 1) * downloadPaginationLimit,
  };

  if (searchConditions.isForAllPartners) {
    query.partnerIds = partnerList.value?.map((partner: any) => partner.id).join(',');
  } else {
    query.partnerId = partnerId.value;
  }

  try {
    const response = await sendGet('rides', query);

    const customRides = response.data.rows.map((ride: any) => {
      ride = rideUtility.formatRideData(ride, true, displayingTimeZone.value);

      return ride;
    });

    downloadData.value = concat(downloadData.value, customRides);

    const requestIds = customRides.map((ride: any) => ride.requestId).filter((requestId: string) => requestId);

    if (requestIds.length) {
      const customChargeRecords = await rideService.fetchRideCustomChargeRecords(requestIds);
      rideUtility.mergeCustomChargeRecordsToRideDownloadData(downloadData.value, customChargeRecords);
    }

    status.isLoading = false;
    if (customRides.length === downloadPaginationLimit) {
      await downloadRidesJSON2CSV(pageIndex + 1);
    } else {
      rideUtility.formatRideDataForDownload(downloadData.value);

      const parser = new Parser({ fields: partnerDownloadFields.value });
      const csv = parser.parse(downloadData.value);

      util.downloadJSONToCSV(csv, `${partnerCode.value}-Rides.csv`);

      downloadData.value = [];
      status.isLoading = false;
    }
  } catch (error) {
    status.resultHtml = util.getErrorResponse(error);
  }
};

const isUnpaidRide = (ride: any) => {
  return (
    ride.paidStatus === 'unpaid' &&
    ride.requestId &&
    ride.status !== 'start' &&
    ride.status !== 'processing' &&
    ride.status !== 'accepted' &&
    ride.status !== 'arriving' &&
    ride.status !== 'in_progress'
  );
};

/*
  This function is recursive function
  because the API returns response regarding with pagination limit
  and we don't know total count
*/
const getOperators = async (offset: number = 0) => {
  try {
    const response = await fetchOperators({
      pagination: {
        offset,
        limit: operatorsPaginationLimit,
      },
    });

    for (const operator of response.rows) {
      operatorsList.value.push({
        id: operator.id,
        key: operator.id,
        label: `${operator.firstName} ${operator.lastName}`,
      });
    }

    // response array size is same with pagination limit, need to check more
    if (response.rows.length === operatorsPaginationLimit) {
      await getOperators(offset + operatorsPaginationLimit);
    }
  } catch (error) {
    console.log(error);
  }
};

const customCellStyle = (param: any) => {
  const { column, row } = param;
  if (column.property === 'createdAt') {
    return {
      'background-color': `${row['createdAtColumnColor']} !important`,
    };
  }

  return null;
};

const resetForm = () => {
  status.resultHtml = '';
  status.timeZoneMode = 'customer';
  rides.value = [];
  timeSpanValue.value = null;
  selectedIndex.value = -1;
  paginationInfo.currentPage = 1;
  paginationInfo.totalPages = 1;

  searchConditions.queryType = queryTypeList.value[0].key;
  searchConditions.queryInfo = '';
  searchConditions.status = '';
  searchConditions.orderedBy = '';
  searchConditions.vendor = '';
  searchConditions.isBuggedRide = false;
  searchConditions.isEmergency = false;
  searchConditions.isForAllPartners = false;

  setRideId(0);
  setRideRequestId('');
};

watch(customerPhoneNumber, (value: string) => {
  resetForm();

  if (value) {
    searchConditions.queryInfo = value;
    getRides();
  }
});

watch(timeSpanValue, (value: any) => {
  if (value) {
    searchConditions.startDateTime = util.formatDateTime(value[0], 'YYYY-MM-DD HH:mm:ss');
    searchConditions.endDateTime = util.formatDateTime(Moment(value[1]).endOf('day'), 'YYYY-MM-DD HH:mm:ss');
  } else {
    searchConditions.startDateTime = '';
    searchConditions.endDateTime = '';
  }
});

watch(
  () => status.timeZoneMode,
  (value: string) => {
    rides.value = rides.value.map((ride: any) => {
      const originalRide = originalRides.value.find((item: any) => item.id === ride.id);

      if (!originalRide) {
        return ride;
      }

      const timeZone = value === 'utc' ? 'UTC' : originalRide.timeZone;

      return {
        ...ride,
        ...rideUtility.formatRideDateTimes(originalRide, timeZone),
      };
    });
  },
);

onMounted(() => {
  // We show GoGo Concierge Fee in the table for only montco partner
  if (partnerCode.value === PartnerCodeName.MONTCO) {
    partnerTableColumns.value.push({
      label: 'GoGo Concierge Fee',
      field: 'gogoFee',
      width: 170,
    });
  }

  // We show Partner Credits, Partner Cost, Customer Credits, Customer Cost in the table for only washington partner
  if (partnerCode.value === PartnerCodeName.WASHINGTON) {
    partnerTableColumns.value.push(
      {
        label: 'Partner Credits',
        field: 'partnerCredits',
        width: 150,
      },
      {
        label: 'Partner Cost',
        field: 'partnerCost',
        width: 120,
      },
      {
        label: 'Customer Credits',
        field: 'customerCredits',
        width: 150,
      },
      {
        label: 'Customer Cost',
        field: 'customerCost',
        width: 130,
      },
    );
  }

  if (partnerCode.value === PartnerCodeName.ACRC) {
    partnerTableColumns.value.push(
      {
        label: 'Pre-Authorization #',
        field: 'preAuthorizationNumber',
        width: 150,
      },
      {
        label: 'UCI #',
        field: 'externalPartnerCallerId',
        width: 120,
      },
    );
    partnerDownloadFields.value.push('preAuthorizationNumber', 'externalPartnerCallerId');
  }

  if (partnerCode.value === PartnerCodeName.CVRC) {
    partnerTableColumns.value.push(
      {
        label: 'Pre-Authorization #',
        field: 'preAuthorizationNumber',
        width: 150,
      },
      {
        label: 'UCI #',
        field: 'externalPartnerCallerId',
        width: 120,
      },
    );
    partnerDownloadFields.value.push('preAuthorizationNumber', 'externalPartnerCallerId');
  }

  if (partnerCode.value === PartnerCodeName.ACEFMS) {
    partnerTableColumns.value.push(
      {
        label: 'UCI #',
        field: 'externalPartnerCallerId',
        width: 120,
      },
      {
        label: 'Service Code',
        field: 'serviceCode',
        width: 120,
      },
    );
    partnerDownloadFields.value.push('externalPartnerCallerId', 'serviceCode');
  }

  if (GVRA_PARTNERS.includes(partnerCode.value)) {
    partnerTableColumns.value.push(
      {
        label: 'Client ID#',
        field: 'externalPartnerCallerId',
        width: 140,
      },
      {
        label: 'Authorization Number',
        field: 'preAuthorizationNumber',
        width: 170,
      },
      {
        label: 'Service Item Code',
        field: 'serviceCode',
        width: 140,
      },
    );
    partnerDownloadFields.value.push(
      'externalPartnerCallerId',
      'preAuthorizationNumber',
      'serviceCode',
    );
  }

  if (ADRS_PARTNERS.includes(partnerCode.value)) {
    partnerTableColumns.value.push(
      {
        label: 'Case Master Id',
        field: 'externalPartnerCallerId',
        width: 150,
      },
    )
    partnerDownloadFields.value.push('externalPartnerCallerId')
  }

  if (VRNH_PARTNERS.includes(partnerCode.value) || ADRS_PARTNERS.includes(partnerCode.value)) {
    partnerTableColumns.value.push(
      {
        label: 'Authorization Number',
        field: 'preAuthorizationNumber',
        width: 150,
      },
      {
        label: 'begin date',
        field: 'beginningDate',
        width: 150,
      },
      {
        label: 'suspension date',
        field: 'suspensionDate',
        width: 150,
      },
      {
        label: 'monthly budget amount',
        field: 'monthlyBudgetAmount',
        width: 150,
      },
      {
        label: 'counselor first name',
        field: 'counselorFirstName',
        width: 150,
      },
      {
        label: 'counselor last name',
        field: 'counselorLastName',
        width: 150,
      },
      {
        label: 'counselor email',
        field: 'counselorEmail',
        width: 150,
      },
      {
        label: 'Total Partner Cost',
        field: 'totalPartnerCost',
        width: 150
      }
    );
    partnerDownloadFields.value.push('preAuthorizationNumber', 'beginningDate', 'suspensionDate', 'monthlyBudgetAmount', 'counselorFirstName', 'counselorLastName', 'counselorEmail', 'totalPartnerCost');
  }

  if (NC_EIPD_PARTNERS.includes(partnerCode.value)) {
    partnerTableColumns.value.push(
      {
        label: 'Authorization Number',
        field: 'preAuthorizationNumber',
        width: 170,
      },
      {
        label: 'Client ID Number',
        field: 'externalPartnerCallerId',
        width: 140,
      },
      {
        label: 'Start Date',
        field: 'beginningDate',
        width: 150,
      },
      {
        label: 'End Date',
        field: 'suspensionDate',
        width: 150,
      },
      {
        label: 'Monthly Budget',
        field: 'monthlyBudgetAmount',
        width: 150,
      },
    );
    partnerDownloadFields.value.push('preAuthorizationNumber', 'externalPartnerCallerId', 'beginningDate', 'suspensionDate', 'monthlyBudgetAmount');
  }

  initQueryTypeList();

  const queryRequestId = (route.query?.requestId as string) || '';
  const queryRideId = (route.query?.rideId as string) || '';

  if (scheduledRideId.value) {
    searchConditions.queryType = 'scheduledRideId';
    searchConditions.queryInfo = scheduledRideId.value.toString();
    setScheduledRideId(0);
  } else if (queryRequestId) {
    searchConditions.queryType = 'request-id';
    searchConditions.queryInfo = queryRequestId;
  } else if (queryRideId) {
    searchConditions.queryType = 'ride-id';
    searchConditions.queryInfo = queryRideId;
  } else {
    searchConditions.queryType = 'phone-number';
    searchConditions.queryInfo = customerPhoneNumber.value;
  }

  if (searchConditions.queryInfo && status.autoLoading) {
    getRides();
  } else if (isPartnerDashboard.value) {
    getRides();
  }

  getOperators();

  events.on('ride-activities-loading', (loading: any) => (status.isRideActivitiesLoading = loading));
});

onBeforeUnmount(() => {
  setRideId(0);
  setRideRequestId('');

  events.off('ride-activities-loading');
});
</script>

<style lang="scss">
.ride-search {
  .info-icon {
    color: #ee682d;
    &.nl-icon {
      margin-top: 10px;
      font-size: 28px;
    }
    &.fill-icon {
      font-size: 28px;
    }
    &.receipt-icon {
      width: 35px;
      height: 35px;
      padding-right: 5px;
      padding-top: 2px;
      margin-top: 3px;
      color: #ef8157;
    }
    &.reorder-icon,
    &.return-ride-icon {
      width: 32px;
      height: 32px;
      padding-left: 4px;
    }
    &.return-ride-icon {
      padding-left: 0;
      margin-top: 2px;
    }
  }
  .gift-icon {
    font-size: 28px;
  }
  .table-view {
    .address-list {
      ol.text-list {
        margin-left: 0;
        padding-left: 10px;
      }
    }
  }
}
</style>
